import { useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'

export const InvitationByToken = graphql(/* GraphQL */ `
  query InvitationByToken($input: InvitationByTokenInput!) {
    invitationByToken(input: $input) {
      id
      email
      organization {
        id
        name
        shouldEnforce2FASetup
      }
      invitedBy {
        name
        email
      }
    }
  }
`)

/**
 * Router location state carried over by the `/invite/accept` redirect (see
 * `AppRoutes.tsx`). Threading the token through `state` keeps the JWT out
 * of the visible URL once the user is dropped on their normal landing page.
 */
interface InviteLocationState {
  inviteJwt?: string
  inviteIsNewUser?: boolean
}

export type InvitationModalVariant = 'accept' | 'welcome'

export interface InvitationModalData {
  /** The invitation JWT, verified server-side by `acceptInvitation`. */
  jwt: string
  /** `welcome` = brand-new Buffer user (intro modal); `accept` = existing. */
  variant: InvitationModalVariant
  organizationId: string
  organizationName: string
  inviterDisplay: string
  /**
   * The inviting org enforces 2FA and this viewer hasn't set it up
   * (server-derived: `Organization.shouldEnforce2FASetup` already accounts
   * for the rollout Split and the viewer's own 2FA state, so it's `false`
   * for invitees who have 2FA configured).
   */
  requiresTwoFactorSetup: boolean
}

export interface UseInvitationModalResult {
  /** Populated once the invitation token resolves; `null` while loading or absent. */
  invitation: InvitationModalData | null
  /**
   * The user landed with an invite token that did not resolve to an
   * invitation — expired, revoked, already used, or forged. Drives the
   * blocking "This invite isn't valid anymore" dialog (CORE-6244).
   */
  isInvalid: boolean
  /** Whether the modal should be open. */
  isOpen: boolean
  /** Close the modal without accepting (e.g. the welcome variant's dismiss). */
  close: () => void
}

/** The pending invitation captured on landing. */
interface CapturedInvite {
  jwt: string
  isNewUser: boolean
}

/**
 * The captured invite, held at **module scope** so it survives not just in-app
 * navigation but a full remount of the tree that hosts the modal.
 *
 * Component-local state is not enough: `LoadingGate` → `AppPages` (which mounts
 * the modal container) is *remounted* as the app shell settles — feature-flag
 * resolution, sidebar swaps, org-context settling. A `useState` capture is wiped
 * on that remount, and by then the token is gone from both the URL and router
 * `state` (`DefaultPath` redirects `/` → the real landing page with no state),
 * so the remounted hook re-captures nothing and the modal vanishes a beat after
 * landing. Module scope outlives the remount. This mirrors how `LoadingGate`
 * captures a `/org/:id` deep-link at module scope for the same reason (see its
 * `pendingDeepLinkOrgId`).
 *
 * `moduleDismissed` records that the user closed the modal, so a later remount
 * (which reseeds `isOpen` from module scope) doesn't reopen it. Both persist
 * until the next hard reload — which is exactly what accepting triggers.
 */
let moduleCapturedInvite: CapturedInvite | null = null
let moduleInviteDismissed = false

/** Narrow react-router's untyped `location.state` to the invite fields. */
function readInviteState(state: unknown): InviteLocationState {
  if (typeof state !== 'object' || state === null) {
    return {}
  }
  const record: Record<string, unknown> = { ...state }
  return {
    inviteJwt:
      typeof record.inviteJwt === 'string' ? record.inviteJwt : undefined,
    inviteIsNewUser: record.inviteIsNewUser === true,
  }
}

/**
 * Extract the invite from the location present at first mount — router `state`
 * from the `/invite/accept` redirect, or a `?invite=`/`?new=` query fallback.
 */
function captureFromLocation(
  state: unknown,
  searchParams: URLSearchParams,
): CapturedInvite | null {
  const s = readInviteState(state)
  const jwt = s.inviteJwt ?? searchParams.get('invite') ?? null
  if (!jwt) {
    return null
  }
  const isNewUser =
    s.inviteIsNewUser === true || searchParams.get('new') === '1'
  return { jwt, isNewUser }
}

/**
 * Test-only: reset the module-level capture between test cases. Module state
 * intentionally persists across renders (that's what survives shell remounts),
 * so tests must reset it explicitly to stay isolated.
 */
export function resetInviteCaptureForTests(): void {
  moduleCapturedInvite = null
  moduleInviteDismissed = false
}

/**
 * Detects a pending invitation the user landed with — either from the
 * `/invite/accept` redirect's router state or a `?invite=` query fallback —
 * and resolves the org/inviter details used to render the invitation modal.
 *
 * The modal replaces the former full-page `/invite/accept` experience: the
 * user is dropped wherever they would normally land and the modal sits on top.
 *
 * The token is **captured once into module scope**, NOT read from the live
 * location on every render, and NOT held only in component state. Two things
 * would otherwise make the modal vanish moments after landing:
 *
 *  1. `DefaultPath` immediately redirects `/` → the real landing page (e.g.
 *     `/schedule`) with no router `state`, so reading `location.state`/query
 *     params reactively loses the token instantly.
 *  2. `LoadingGate`/`AppPages` (which host the modal) get *remounted* as the app
 *     shell settles, wiping any component-local capture.
 *
 * Capturing to module scope decouples the modal's lifetime from both: it stays
 * up across in-app navigation AND shell remounts until the user accepts (hard
 * reload) or dismisses it. Mirrors `LoadingGate`'s `pendingDeepLinkOrgId`.
 */
export const useInvitationModal = (): UseInvitationModalResult => {
  // Read location/search only for the initial capture. The container that
  // calls this hook is a persistent sibling of <Routes> mounted at
  // `/invite/accept`, so this first render sees the invite query params (and
  // any router state from the redirect) before DefaultPath navigates away.
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Seed from the durable module-level capture, falling back to the location on
  // first mount (before any capture exists). Writing to module scope in the lazy
  // initializer means a subsequent shell remount reseeds from it rather than
  // re-reading a location that no longer carries the token.
  const [captured] = useState<CapturedInvite | null>(() => {
    if (moduleCapturedInvite) {
      return moduleCapturedInvite
    }
    const fromLocation = captureFromLocation(location.state, searchParams)
    if (fromLocation) {
      moduleCapturedInvite = fromLocation
    }
    return fromLocation
  })

  const [isOpen, setIsOpen] = useState(
    () => captured !== null && !moduleInviteDismissed,
  )

  const { data, error, loading } = useQuery(InvitationByToken, {
    variables: { input: { jwt: captured?.jwt ?? '' } },
    skip: !captured,
    fetchPolicy: 'network-only',
  })

  const close = (): void => {
    // Persist the dismissal so a shell remount (which reseeds `isOpen` from
    // module scope) doesn't reopen the modal.
    moduleInviteDismissed = true
    setIsOpen(false)
  }

  const resolved = data?.invitationByToken
  const invitation: InvitationModalData | null =
    captured && resolved
      ? {
          jwt: captured.jwt,
          variant: captured.isNewUser ? 'welcome' : 'accept',
          organizationId: resolved.organization.id,
          organizationName: resolved.organization.name,
          inviterDisplay:
            resolved.invitedBy?.name ??
            resolved.invitedBy?.email ??
            'A teammate',
          requiresTwoFactorSetup: resolved.organization.shouldEnforce2FASetup,
        }
      : null

  // The server rejects expired/revoked/used/forged tokens as a thrown GraphQL
  // error (never an `isValid: false` payload), so any settled-without-an-
  // invitation state means the invite is no longer actionable.
  const isInvalid =
    captured !== null && !loading && (error !== undefined || !resolved)

  return { invitation, isInvalid, isOpen, close }
}
